import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, concatMap, groupBy, map, mergeMap, of, switchMap, tap } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

import { AlertService } from '../../Services/alert.service';
import { RbacService } from '../../Services/rbac.service';
import {
  RbacEffectivePermissionsActions,
  RbacMePermissionsActions,
  RbacPermissionsActions,
  RbacRolePermissionsActions,
  RbacRolesActions,
  RbacSodRulesActions,
  RbacUserScopesActions,
  RbacUsersActions,
} from './rbac-sod.actions';

/**
 * RBAC + SoD feature effects (US #9320 / R1 #9357).
 *
 * One effect per resource × operation. Load effects use `switchMap` (only the
 * latest load matters — a fresh `Load` should cancel an in-flight one). CRUD
 * effects use `mergeMap` so concurrent admin clicks (e.g. ticking three R3
 * grid cells in quick succession) don't cancel each other.
 */

/**
 * Normalise a thrown value to the `string` the reducer's `error` field renders.
 *
 * `HttpErrorResponse` is handled FIRST and explicitly: Angular's
 * `HttpErrorResponse` *implements* `Error` but does NOT *extend* it, so
 * `err instanceof Error` is `false` for it — the old code fell through to
 * `String(err)`, which stringifies to the useless `"[object Object]"` (the
 * masked RBAC load failures). Surface the server's message when present, else
 * the HTTP status line; fall back to `Error.message` / `String()` otherwise.
 */
function toErrorMessage(err: unknown): string {
  if (err instanceof HttpErrorResponse) {
    const body = err.error as { message?: string; error?: string } | string | null;
    const serverMsg =
      typeof body === 'string' ? body : body?.message ?? body?.error ?? '';
    const detail = (serverMsg ?? '').toString().trim();
    return detail.length > 0 ? detail : `HTTP ${err.status} ${err.statusText}`.trim();
  }
  return err instanceof Error ? err.message : String(err);
}

/**
 * Per-write success copy, keyed by the resolved action `type`. Module-level
 * (like `toErrorMessage`) since it's a static lookup, not instance state.
 */
const WRITE_SUCCESS_MESSAGES: Record<string, string> = {
  [RbacPermissionsActions.createSuccess.type]: 'Permission created successfully !',
  [RbacPermissionsActions.updateSuccess.type]: 'Permission updated successfully !',
  [RbacPermissionsActions.deleteSuccess.type]: 'Permission deleted successfully !',
  [RbacSodRulesActions.createSuccess.type]: 'SoD rule created successfully !',
  [RbacSodRulesActions.updateSuccess.type]: 'SoD rule updated successfully !',
  [RbacSodRulesActions.deleteSuccess.type]: 'SoD rule deleted successfully !',
  [RbacUserScopesActions.createSuccess.type]: 'Scope added successfully !',
  [RbacUserScopesActions.updateSuccess.type]: 'Scope updated successfully !',
  [RbacUserScopesActions.deleteSuccess.type]: 'Scope deleted successfully !',
  [RbacRolePermissionsActions.assignSuccess.type]: 'Permission assigned successfully !',
  [RbacRolePermissionsActions.unassignSuccess.type]: 'Permission unassigned successfully !',
  [RbacUsersActions.assignRoleSuccess.type]: 'Role assigned successfully !',
  [RbacUsersActions.unassignRoleSuccess.type]: 'Role removed successfully !',
  [RbacRolesActions.reorderSuccess.type]: 'Escalation hierarchy saved !',
};

@Injectable()
export class RbacSodEffects {
  private readonly actions$ = inject(Actions);
  private readonly rbacService = inject(RbacService);
  private readonly alertService = inject(AlertService);
  private readonly translate = inject(TranslateService);

  // ─────────────────────── Roles (read-only) ────────────────────────────────

  loadRoles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RbacRolesActions.load),
      switchMap(() =>
        this.rbacService.getRoles().pipe(
          map((items) => RbacRolesActions.loadSuccess({ items })),
          catchError((err) =>
            of(RbacRolesActions.loadFailure({ error: toErrorMessage(err) })),
          ),
        ),
      ),
    ),
  );

  // ── Reorder escalation hierarchy (US #9320) ─────────────────────────────
  // Writes the role seniority ladder then resolves to the re-read catalog.
  // `concatMap`: a Save is a single full-ladder write — serialize so a slow
  // earlier save can't clobber a newer one with stale ranks.
  reorderRoles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RbacRolesActions.reorder),
      concatMap(({ rankedRoleIds }) =>
        this.rbacService.updateRoleRanks(rankedRoleIds).pipe(
          map((items) => RbacRolesActions.reorderSuccess({ items })),
          catchError((err) =>
            of(RbacRolesActions.reorderFailure({ error: toErrorMessage(err) })),
          ),
        ),
      ),
    ),
  );

  // ─────────────────────── Users (read-only) ────────────────────────────────

  loadUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RbacUsersActions.load),
      switchMap(() =>
        this.rbacService.getUsers().pipe(
          map((items) => RbacUsersActions.loadSuccess({ items })),
          catchError((err) =>
            of(RbacUsersActions.loadFailure({ error: toErrorMessage(err) })),
          ),
        ),
      ),
    ),
  );

  // ── Role assign / unassign (R4 "Roles" card) ─────────────────────────────
  // Unlike the other CRUD effects (which operate on independent records by id),
  // both of these resolve to the *whole* updated `User` and the reducer
  // `upsertOne`s it. A plain `mergeMap` would let two requests for the SAME user
  // race — a late-arriving older snapshot could clobber a newer one and drop a
  // role from the store until the next refresh. So we serialize per-user with
  // `groupBy(userId)` + `concatMap` (one user's add/remove clicks run in order),
  // while different users still run concurrently via the outer `mergeMap`. This
  // holds regardless of the eventual live response shape (GAP R-4).

  assignUserRole$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RbacUsersActions.assignRole),
      groupBy(({ userId }) => userId),
      mergeMap((group$) =>
        group$.pipe(
          concatMap(({ userId, roleId }) =>
            this.rbacService.assignUserRole(userId, roleId).pipe(
              map((user) => RbacUsersActions.assignRoleSuccess({ user })),
              catchError((err) =>
                of(RbacUsersActions.assignRoleFailure({ error: toErrorMessage(err) })),
              ),
            ),
          ),
        ),
      ),
    ),
  );

  unassignUserRole$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RbacUsersActions.unassignRole),
      groupBy(({ userId }) => userId),
      mergeMap((group$) =>
        group$.pipe(
          concatMap(({ userId, roleId }) =>
            this.rbacService.unassignUserRole(userId, roleId).pipe(
              map((user) => RbacUsersActions.unassignRoleSuccess({ user })),
              catchError((err) =>
                of(RbacUsersActions.unassignRoleFailure({ error: toErrorMessage(err) })),
              ),
            ),
          ),
        ),
      ),
    ),
  );

  /**
   * A user's role set feeds their resolved permissions, so any role change
   * invalidates R6's cached `effective` snapshot for that user. Evict it so a
   * subsequent preview re-resolves fresh (mirrors the documented purpose of
   * `EffectivePermissions.clear`).
   */
  evictEffectiveOnRoleChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        RbacUsersActions.assignRoleSuccess,
        RbacUsersActions.unassignRoleSuccess,
      ),
      map(({ user }) =>
        RbacEffectivePermissionsActions.clear({ userId: user.userId }),
      ),
    ),
  );

  // ───────────────────── Permissions (full CRUD) ────────────────────────────

  loadPermissions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RbacPermissionsActions.load),
      switchMap(() =>
        this.rbacService.getPermissions().pipe(
          map((items) => RbacPermissionsActions.loadSuccess({ items })),
          catchError((err) =>
            of(RbacPermissionsActions.loadFailure({ error: toErrorMessage(err) })),
          ),
        ),
      ),
    ),
  );

  createPermission$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RbacPermissionsActions.create),
      mergeMap(({ payload }) =>
        this.rbacService.createPermission(payload).pipe(
          map((item) => RbacPermissionsActions.createSuccess({ item })),
          catchError((err) =>
            of(RbacPermissionsActions.createFailure({ error: toErrorMessage(err) })),
          ),
        ),
      ),
    ),
  );

  updatePermission$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RbacPermissionsActions.update),
      mergeMap(({ payload }) =>
        this.rbacService.updatePermission(payload).pipe(
          map((item) => RbacPermissionsActions.updateSuccess({ item })),
          catchError((err) =>
            of(RbacPermissionsActions.updateFailure({ error: toErrorMessage(err) })),
          ),
        ),
      ),
    ),
  );

  deletePermission$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RbacPermissionsActions.delete),
      mergeMap(({ permissionId }) =>
        this.rbacService.deletePermission(permissionId).pipe(
          map(() => RbacPermissionsActions.deleteSuccess({ permissionId })),
          catchError((err) =>
            of(RbacPermissionsActions.deleteFailure({ error: toErrorMessage(err) })),
          ),
        ),
      ),
    ),
  );

  // ─────────────────────── SoD rules (full CRUD) ────────────────────────────

  loadSodRules$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RbacSodRulesActions.load),
      switchMap(() =>
        this.rbacService.getSodRules().pipe(
          map((items) => RbacSodRulesActions.loadSuccess({ items })),
          catchError((err) =>
            of(RbacSodRulesActions.loadFailure({ error: toErrorMessage(err) })),
          ),
        ),
      ),
    ),
  );

  createSodRule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RbacSodRulesActions.create),
      mergeMap(({ payload }) =>
        this.rbacService.createSodRule(payload).pipe(
          map((item) => RbacSodRulesActions.createSuccess({ item })),
          catchError((err) =>
            of(RbacSodRulesActions.createFailure({ error: toErrorMessage(err) })),
          ),
        ),
      ),
    ),
  );

  updateSodRule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RbacSodRulesActions.update),
      mergeMap(({ payload }) =>
        this.rbacService.updateSodRule(payload).pipe(
          map((item) => RbacSodRulesActions.updateSuccess({ item })),
          catchError((err) =>
            of(RbacSodRulesActions.updateFailure({ error: toErrorMessage(err) })),
          ),
        ),
      ),
    ),
  );

  deleteSodRule$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RbacSodRulesActions.delete),
      mergeMap(({ sodRuleId }) =>
        this.rbacService.deleteSodRule(sodRuleId).pipe(
          map(() => RbacSodRulesActions.deleteSuccess({ sodRuleId })),
          catchError((err) =>
            of(RbacSodRulesActions.deleteFailure({ error: toErrorMessage(err) })),
          ),
        ),
      ),
    ),
  );

  // ───────────────── Role × Permission (assign/unassign) ────────────────────

  loadRolePermissions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RbacRolePermissionsActions.load),
      switchMap(() =>
        this.rbacService.getRolePermissions().pipe(
          map((items) => RbacRolePermissionsActions.loadSuccess({ items })),
          catchError((err) =>
            of(RbacRolePermissionsActions.loadFailure({ error: toErrorMessage(err) })),
          ),
        ),
      ),
    ),
  );

  assignRolePermission$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RbacRolePermissionsActions.assign),
      mergeMap(({ payload }) =>
        this.rbacService.assignRolePermission(payload).pipe(
          map((item) => RbacRolePermissionsActions.assignSuccess({ item })),
          catchError((err) =>
            of(RbacRolePermissionsActions.assignFailure({ error: toErrorMessage(err) })),
          ),
        ),
      ),
    ),
  );

  unassignRolePermission$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RbacRolePermissionsActions.unassign),
      mergeMap(({ rolePermissionId }) =>
        this.rbacService.unassignRolePermission(rolePermissionId).pipe(
          map(() => RbacRolePermissionsActions.unassignSuccess({ rolePermissionId })),
          catchError((err) =>
            of(RbacRolePermissionsActions.unassignFailure({ error: toErrorMessage(err) })),
          ),
        ),
      ),
    ),
  );

  // ─────────────────────── User scopes (full CRUD) ──────────────────────────

  loadUserScopes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RbacUserScopesActions.load),
      switchMap(({ userId }) =>
        this.rbacService.getUserScopes(userId).pipe(
          map((items) => RbacUserScopesActions.loadSuccess({ items })),
          catchError((err) =>
            of(RbacUserScopesActions.loadFailure({ error: toErrorMessage(err) })),
          ),
        ),
      ),
    ),
  );

  createUserScope$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RbacUserScopesActions.create),
      mergeMap(({ payload }) =>
        this.rbacService.createUserScope(payload).pipe(
          map((item) => RbacUserScopesActions.createSuccess({ item })),
          catchError((err) =>
            of(RbacUserScopesActions.createFailure({ error: toErrorMessage(err) })),
          ),
        ),
      ),
    ),
  );

  updateUserScope$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RbacUserScopesActions.update),
      mergeMap(({ payload }) =>
        this.rbacService.updateUserScope(payload).pipe(
          map((item) => RbacUserScopesActions.updateSuccess({ item })),
          catchError((err) =>
            of(RbacUserScopesActions.updateFailure({ error: toErrorMessage(err) })),
          ),
        ),
      ),
    ),
  );

  deleteUserScope$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RbacUserScopesActions.delete),
      mergeMap(({ userScopeId }) =>
        this.rbacService.deleteUserScope(userScopeId).pipe(
          map(() => RbacUserScopesActions.deleteSuccess({ userScopeId })),
          catchError((err) =>
            of(RbacUserScopesActions.deleteFailure({ error: toErrorMessage(err) })),
          ),
        ),
      ),
    ),
  );

  // ─────────────────────── MePermissions (singleton) ────────────────────────

  loadMePermissions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RbacMePermissionsActions.load),
      switchMap(() =>
        this.rbacService.getMePermissions().pipe(
          map((data) => RbacMePermissionsActions.loadSuccess({ data })),
          catchError((err) =>
            of(RbacMePermissionsActions.loadFailure({ error: toErrorMessage(err) })),
          ),
        ),
      ),
    ),
  );

  // ──────────── EffectivePermissions (keyed-by-userId, mergeMap) ────────────

  /**
   * `mergeMap` (not `switchMap`) so R6 previews for multiple users can run in
   * parallel without cancelling each other — each load carries its own
   * `userId` and lands in its own cache slot in the reducer.
   */
  loadEffectivePermissions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RbacEffectivePermissionsActions.load),
      mergeMap(({ userId }) =>
        this.rbacService.getEffectivePermissions(userId).pipe(
          map((data) => RbacEffectivePermissionsActions.loadSuccess({ userId, data })),
          catchError((err) =>
            of(
              RbacEffectivePermissionsActions.loadFailure({
                userId,
                error: toErrorMessage(err),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  // ───────────────────────── Write notifications ────────────────────────────
  // Centralised success / error snack-bars for every RBAC + SoD write. A single
  // pair of non-dispatching effects covers all four writable resources, so the
  // R2–R5 components don't each have to wire their own toast. Loads are
  // deliberately excluded — a failed load already surfaces inline (empty state /
  // "No record found"), and a success toast on every tab entry would be noise.

  notifyWriteSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          RbacPermissionsActions.createSuccess,
          RbacPermissionsActions.updateSuccess,
          RbacPermissionsActions.deleteSuccess,
          RbacSodRulesActions.createSuccess,
          RbacSodRulesActions.updateSuccess,
          RbacSodRulesActions.deleteSuccess,
          RbacUserScopesActions.createSuccess,
          RbacUserScopesActions.updateSuccess,
          RbacUserScopesActions.deleteSuccess,
          RbacRolePermissionsActions.assignSuccess,
          RbacRolePermissionsActions.unassignSuccess,
          RbacUsersActions.assignRoleSuccess,
          RbacUsersActions.unassignRoleSuccess,
          RbacRolesActions.reorderSuccess,
        ),
        tap((action) => {
          const message = WRITE_SUCCESS_MESSAGES[action.type] ?? 'Saved successfully !';
          this.alertService.openAlert(this.translate.instant(message), 'success');
        }),
      ),
    { dispatch: false },
  );

  notifyWriteFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          RbacPermissionsActions.createFailure,
          RbacPermissionsActions.updateFailure,
          RbacPermissionsActions.deleteFailure,
          RbacSodRulesActions.createFailure,
          RbacSodRulesActions.updateFailure,
          RbacSodRulesActions.deleteFailure,
          RbacUserScopesActions.createFailure,
          RbacUserScopesActions.updateFailure,
          RbacUserScopesActions.deleteFailure,
          RbacRolePermissionsActions.assignFailure,
          RbacRolePermissionsActions.unassignFailure,
          RbacUsersActions.assignRoleFailure,
          RbacUsersActions.unassignRoleFailure,
          RbacRolesActions.reorderFailure,
        ),
        tap((action) => {
          const serverMsg = action.error?.trim();
          const message =
            serverMsg && serverMsg.length > 0
              ? serverMsg
              : 'Something went wrong, please try again later !';
          this.alertService.openAlert(this.translate.instant(message), 'error');
        }),
      ),
    { dispatch: false },
  );
}
